import React, { useCallback } from 'react';

import {
  gridRowsLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { useRequestReportExport } from 'src/components/repository/RepositoryActions';
import { getIsExtractionJobStale } from 'src/editor';
import { Apollo } from 'src/gql/generated';

import { ConfirmationDialog, ConfirmationDialogVariant } from '../generic';
import type { ReportGridRow } from '../reports';
import { getIsReportGridRow } from '../reports';

export function ExportExtractionResultsWarningModal({
  onClose,
  open,
  reportId,
}: {
  onClose: () => void;
  open: boolean;
  reportId: UUID;
}) {
  const { requestReportExport } = useRequestReportExport();

  return (
    <ConfirmationDialog
      message="AI extraction has not been run or may be outdated for some of the documents you are about
          to export. This means your export may contain inaccurate data. Re-run questions to obtain
          accurate data and then export."
      open={open}
      primaryText="Export Anyways"
      title="AI Extraction Warning"
      variant={ConfirmationDialogVariant.Warn}
      onClickPrimary={async () => {
        await requestReportExport({ format: Apollo.ExportFormat.Xlsx, reportId });
      }}
      onClose={onClose}
    />
  );
}

export enum ExtractionStatus {
  NotRunOnSignedDoc = 'not-run',
  Ok = 'good',
  Stale = 'stale',
}

export function useGetExtractionStatus(): {
  getExtractionStatus: (row: ReportGridRow) => ExtractionStatus;
} {
  const getExtractionStatus = useCallback((row: ReportGridRow): ExtractionStatus => {
    if (row.isSigned && !row.questionJobs?.length) return ExtractionStatus.NotRunOnSignedDoc;

    const isExtractionStale = row.questionJobs?.some(job => getIsExtractionJobStale(row, job));
    return isExtractionStale ? ExtractionStatus.Stale : ExtractionStatus.Ok;
  }, []);

  return { getExtractionStatus };
}

export function useExtractionReportWarning() {
  const apiRef = useGridApiContext();
  const { getExtractionStatus } = useGetExtractionStatus();
  const gridRowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
  const getAreExtractionsStale = useCallback(() => {
    const rows = Object.values(gridRowsLookup).filter(getIsReportGridRow);
    return rows.some(row => {
      const status = getExtractionStatus(row);
      return status !== ExtractionStatus.Ok;
    });
  }, [gridRowsLookup, getExtractionStatus]);

  return { getAreExtractionsStale };
}
